// [PROSPEK] 2026-06-05 Pure module for Prospek sidebar nav gating — single source of truth.
// Extracted from Sidebar.vue so that vitest tests exercise the REAL gating logic,
// not a replica. Any change to Prospek nav items goes here.
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

/**
 * Build the Prospek-specific sidebar nav items based on feature flags.
 *
 * @param {Object} params
 * @param {Function} params.isFeatureEnabled - (accountId, flag) => boolean
 * @param {number}   params.accountId        - Current account ID
 * @param {Function} params.t                - i18n translator, t(key) => string
 * @param {Function} params.routeBuilder     - accountScopedRoute(name, params?) => route object
 * @returns {Array} Nav item objects for the Sidebar.vue menuItems array
 */
export const buildProspekNavItems = ({
  isFeatureEnabled,
  accountId,
  t,
  routeBuilder,
}) => {
  const items = [];

  // [PROSPEK] Pipeline (Kanban) board sidebar entry
  if (isFeatureEnabled(accountId, FEATURE_FLAGS.KANBAN_BOARD)) {
    items.push({
      name: 'Pipeline',
      label: t('SIDEBAR.KANBAN'),
      icon: 'i-lucide-columns-3',
      to: routeBuilder('pipeline_board'),
      activeOn: ['pipeline_board', 'pipeline_board_conversation'],
      // [PROSPEK] 2026-06-05 data-testid hook for test assertions
      dataTestId: 'prospek-nav-pipeline',
      // [/PROSPEK]
    });
  }
  // [/PROSPEK]

  // [PROSPEK] Brain knowledge management sidebar
  if (isFeatureEnabled(accountId, FEATURE_FLAGS.BRAIN)) {
    items.push({
      name: 'Brain',
      label: t('SIDEBAR.BRAIN'),
      icon: 'i-lucide-brain',
      to: routeBuilder('brain_assistants_index', {
        navigationPath: 'brain_assistants_faqs_index',
      }),
      activeOn: [
        'brain_assistants_index',
        'brain_assistants_faqs_index',
        'brain_assistants_faqs_pending',
        'brain_assistants_knowledge_index',
        'brain_assistants_knowledge_pending',
        'brain_assistants_inboxes_index',
        'brain_assistants_settings_index',
        'brain_assistants_data_manager_settings',
      ],
      // [PROSPEK] 2026-06-05 data-testid hook for test assertions
      dataTestId: 'prospek-nav-brain',
      // [/PROSPEK]
      children: [
        {
          name: 'FAQs',
          label: t('SIDEBAR.BRAIN_FAQS'),
          activeOn: [
            'brain_assistants_faqs_index',
            'brain_assistants_faqs_pending',
          ],
          to: routeBuilder('brain_assistants_index', {
            navigationPath: 'brain_assistants_faqs_index',
          }),
        },
        {
          name: 'Knowledge',
          label: t('SIDEBAR.BRAIN_KNOWLEDGE'),
          activeOn: [
            'brain_assistants_knowledge_index',
            'brain_assistants_knowledge_pending',
          ],
          to: routeBuilder('brain_assistants_index', {
            navigationPath: 'brain_assistants_knowledge_index',
          }),
        },
        {
          name: 'Inboxes',
          label: t('SIDEBAR.BRAIN_INBOXES'),
          activeOn: ['brain_assistants_inboxes_index'],
          to: routeBuilder('brain_assistants_index', {
            navigationPath: 'brain_assistants_inboxes_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.BRAIN_SETTINGS'),
          activeOn: ['brain_assistants_settings_index'],
          to: routeBuilder('brain_assistants_index', {
            navigationPath: 'brain_assistants_settings_index',
          }),
        },
        ...(isFeatureEnabled(accountId, FEATURE_FLAGS.DATA_MANAGER)
          ? [
              {
                name: 'Data Manager',
                label: t('SIDEBAR.BRAIN_DATA_MANAGER'),
                activeOn: ['brain_assistants_data_manager_settings'],
                to: routeBuilder('brain_assistants_index', {
                  navigationPath: 'brain_assistants_data_manager_settings',
                }),
              },
            ]
          : []),
      ],
    });
  }
  // [/PROSPEK]

  return items;
};
// [/PROSPEK]
